import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Map, List } from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { get as getConfig } from 'config'
import _toLower from 'lodash/toLower'
import { selectIsBreakpoint } from 'services/app'
import { selectAuth, selectAuthorized } from 'services/auth'
import { selectLocation, selectPath, selectResolver } from 'services/resolver/selectors'
import { isPartialPathAllowed, isShop } from 'services/url'
import { selectUserLanguages } from 'services/user'
import { setDefaultGa4Event, setAiGa4Event } from 'services/event-tracking/actions'
import { createGaiaScreenModel } from 'services/resolver'
import { CONTEXT_TYPE_NAV_PRIMARY, SCREEN_TYPE_HEADER, createUpstreamContext } from 'services/upstream-context'
import { clearUpstreamContext } from 'services/upstream-context/actions'
import { LIVE_ACCESS_SUBSCRIPTION_CODE } from 'services/live-access-events/constants'
import { selectHeader } from 'services/menu/selectors'
import { selectTestarossa } from 'services/testarossa/selectors'
import { selectAlertBar } from 'services/alert-bar/selectors'
import {
  NAVIGATION_EVENT_LABELS,
  getNavClickedGa4Event,
  AI_NAVIGATION_CLICKED_GA4_EVENT,
} from 'services/event-tracking'
import { selectStaticTextByKey } from 'services/static-text/selectors'
import { selectUserReferrals } from 'services/referral/selectors'
import { showInviteFriendPopup } from 'services/referral/actions'
import ToolTipArrow, { DIRECTION_UP } from 'components/ToolTipArrow'
import SearchForm from 'components/SearchForm'
import Link, { TARGET_BLANK, TextLink } from 'components/Link'
import Button, { TYPES } from 'components/Button'
import { setNavigationOverlayVisible, setNotificationsMenuVisible, setUserMenuVisible } from 'components/Header/actions'
import { headerAlertBarShouldRender } from 'components/AlertBar/connect'
import {
  ES,
  FR,
  DE,
  EN,
} from 'services/languages/constants'
import {
  URL_MARKETPLACE_LOGGED_IN,
  URL_GAIA_PLUS,
  URL_GAIA_PLUS_EVENTS,
  URL_AI_CHAT,
} from 'services/url/constants'
// import Tour from 'components/Tour'
import Announcement from 'components/Announcement'
import { DaytonaCase, DaytonaSwitch, DaytonaDefault } from 'components/Daytona'
import { TestarossaCase, TestarossaSwitch, TestarossaDefault } from 'components/Testarossa'
import MobileNavItem, { rearrangeGaiaPlusItem } from './MobileNavItem'

const MOBILE_MENU_BREAKPOINT = 1280
const MOBILE_MENU_BREAKPOINT_ES = 1060
const MOBILE_MENU_BREAKPOINT_FR = 1000
const MOBILE_MENU_BREAKPOINT_DE = 1000

const isShopLinkEnabled = !!getConfig().features.navShopLinkV3

/**
 * Function determinants the mobile breakpoint per language
 * @param {String} language - the user's language
 * @returns {Number} The mobile breakpoint value
 */
function getMobileBreakpoint (language) {
  if (language === ES) {
    return MOBILE_MENU_BREAKPOINT_ES
  }

  if (language === FR) {
    return MOBILE_MENU_BREAKPOINT_FR
  }

  if (language === DE) {
    return MOBILE_MENU_BREAKPOINT_DE
  }

  return MOBILE_MENU_BREAKPOINT
}

/**
 * Function determinants should item has autorized uset or not
 * @param {Map} item Menu item
 * @param {Map} auth The auth state
 * @returns {Boolean}
 */
const isAuthorized = (item = Map(), auth = Map()) => {
  const authUid = auth.get('uid', null)
  const authorize = item.get('auth', null)

  return authorize === null || (authorize && authUid) || (!authorize && !authUid)
}

/**
 * Function determinants is item active or not
 * @param {Map} item Menu item
 * @param {String} pathname Current page pathname
 * @param {Map} auth The auth state
 * @returns {Boolean}
 */
const isActiveItem = (item = Map(), pathname, auth = Map()) => {
  if (isAuthorized(item, auth)) {
    const itemUrl = item.get('url')
    const isActive = itemUrl === pathname || isPartialPathAllowed(pathname, itemUrl)
    const children = item.get('children', List())

    if (!isActive && children && children.size) {
      return children.find((child) => isActiveItem(child, pathname))
    }

    return isActive
  }

  return false
}

/**
 * Function compiles the className
 * @param {Boolean} isActive Is current item active in menu
 * @param {Object} hasChildren Does current item have children
 * @returns {String}
 */
const getItemClassName = (isActive, hasChildren = false) => {
  const className = ['navigation-primary-v2__item']

  if (isActive) className.push('active')
  if (hasChildren) className.push('has-children')

  return className.join(' ')
}

/**
 * Desktop menu view component
 * @param {Object} props The props
 * @param {Map} props.childrenItems The children menu item
 * @param {String} props.trackingId The navItems tracking id
 * @param {(eventLabel: String) => void} props.handleItemClick, Callback for click on item
 * @param {Boolean} props.isActive Is current item active in menu
 * @returns {JSX.Element}
 */
const DesktopNavChildren = ({
  childrenItems,
  trackingId,
  handleItemClick,
  isActive,
}) => {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectPath)
  const language = useSelector(selectUserLanguages).get(0, EN)

  const isUserLivePlan = auth.get('subscriptions') && auth.get('subscriptions').includes(LIVE_ACCESS_SUBSCRIPTION_CODE)

  return (
    <ul
      className={`navigation-primary-v2__secondary ${isActive ? 'active-nav' : ''}`}
      id={trackingId}
      aria-expanded={isActive}
    >
      {childrenItems.map((child, childIndex) => {
        const childTrackingId = child.get('trackingId')

        const { label, url } = rearrangeGaiaPlusItem(child, isUserLivePlan, language)

        return (
          <li
            key={childTrackingId || `child-item-${childIndex}`}
            className={location === url ? getItemClassName(true) : 'navigation-primary-v2__item navigation-primary-v2__item--url'}
            role="heading"
          >
            <TextLink
              to={url}
              text={label}
              data-href={url}
              data-id={childTrackingId}
              onClick={() => handleItemClick(`${childTrackingId} - ${_toLower(label)}`)}
            />
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Desktop menu item component
 * @param {Object} props The props
 * @param {Map} props.item The navItem
 * @param {(eventLabel: String) => void} props.handleItemClick, Callback for click on item
 * @returns {JSX.Element}
 */
const DesktopNavItem = ({ item, handleItemClick }) => {
  const pathname = useSelector(selectPath)
  const auth = useSelector(selectAuth)
  const isLoggedIn = useSelector(selectAuthorized)
  const language = useSelector(selectUserLanguages).get(0, EN)
  const dispatch = useDispatch()

  const isUserLivePlan = auth.get('subscriptions') && auth.get('subscriptions').includes(LIVE_ACCESS_SUBSCRIPTION_CODE)

  const trackingId = item.get('trackingId')
  const isActive = isActiveItem(item, pathname, auth)
  const children = item.get('children', List()) || List()
  const hasChildren = children.size > 0
  const label = item.get('label') || ''
  let url = item.get('url') || ''
  const className = getItemClassName(isActive, hasChildren)
  const isShopLink = isShop(url)

  if (isShopLink && isLoggedIn) {
    url = URL_MARKETPLACE_LOGGED_IN
  }

  if (isShopLink && !isShopLinkEnabled) {
    return null
  }

  if (url === URL_GAIA_PLUS && isUserLivePlan && language === EN) {
    url = URL_GAIA_PLUS_EVENTS
  }

  if (url === URL_AI_CHAT) {
    const aiChatLink = (
      <li key={trackingId} className={className}>
        <Link
          to={url} className="navigation-primary-v2__ai-chat-link"
          onClick={() => dispatch(setAiGa4Event(AI_NAVIGATION_CLICKED_GA4_EVENT))}
        >
          <span className="navigation-primary-v2__ai-chat-link-label">
            {label}
          </span>
          <sup className="navigation-primary-v2__ai-chat-link-superscript">
            Beta
          </sup>
        </Link>
      </li>
    )

    return (
      <>
        <DaytonaSwitch>
          <DaytonaCase campaign="AI-1" unwrap>
            {() => (
              aiChatLink
            )}
          </DaytonaCase>
          <DaytonaDefault unwrap>
            {null}
          </DaytonaDefault>
        </DaytonaSwitch>
        <TestarossaSwitch>
          <TestarossaCase campaign="CP-8" variation={1} unwrap>
            {() => (
              aiChatLink
            )}
          </TestarossaCase>
          <TestarossaCase campaign="CP-10" variation={1} unwrap>
            {() => (
              aiChatLink
            )}
          </TestarossaCase>
          <TestarossaCase campaign="CP-11" variation={1} unwrap>
            {() => (
              aiChatLink
            )}
          </TestarossaCase>
          <TestarossaDefault unwrap>
            {null}
          </TestarossaDefault>
        </TestarossaSwitch>
      </>
    )
  }

  return (
    <li key={trackingId} className={className}>
      <TextLink
        to={url}
        text={label}
        data-href={url}
        onClick={() => handleItemClick(`${trackingId} - ${_toLower(label)}`)}
        data-id={trackingId}
        directLink={isShopLink}
        target={isShopLink ? TARGET_BLANK : null}
      />
      {hasChildren && (
        <DesktopNavChildren
          childrenItems={children}
          trackingId={trackingId}
          handleItemClick={handleItemClick}
          isActive={isActive}
        />
      )}
    </li>
  )
}

/**
 * Desktop menu view component
 * @param {Object} props The props
 * @param {Object} props.history The history of app
 * @param {List} props.navItems The navItems from BE
 * @param {(eventLabel: String) => void} props.handleItemClick, Callback for click on item
 * @param {Function} props.handleSearch, Callback for searching, close all popups
 * @param {String} props.navVersion, Nav version, used for GA4 event nav_version reporting
 * @param {boolean} props.darkMode, Valur indicating if dark mode is enabled
 * @returns {JSX.Element}
 */
const DesktopNav = ({
  history,
  navItems = List(),
  handleItemClick,
  handleSearch,
  navVersion,
  darkMode,
  hideNavBar,
}) => {
  const dispatch = useDispatch()

  if (hideNavBar) {
    return null
  }

  return (
    <div id="navigation-primary-v2__desktop" className="navigation-primary-v2__desktop">
      <ToolTipArrow
        direction={DIRECTION_UP}
        activeArrow="navigation-menu-arrow"
      />
      <ul className="navigation-primary-v2__primary">
        {navItems.map((item, index) => (
          <DesktopNavItem
            key={item.get('trackingId', index)}
            item={item}
            handleItemClick={handleItemClick}
          />
        ))}
        <li className="navigation-primary-v2__item navigation-primary-v2__item--search">
          <SearchForm
            onSubmit={handleSearch}
            history={history}
            resetOnSubmit
            hasButton
            isNav
            onFocus={() => {
              // GA4 event
              dispatch(setDefaultGa4Event(getNavClickedGa4Event(navVersion).mergeDeep({
                nav_element: 'search',
              })))
            }}
            darkMode={darkMode}
          />
        </li>
      </ul>
    </div>
  )
}

/**
 * Mobile menu view component
 * @param {Object} props The props
 * @param {Object} props.history The history of app
 * @param {List} props.navItems The navItems from BE
 * @param {(eventLabel: String) => void} props.handleItemClick, Callback for click on item
 * @param {Function} props.handleSearch, Callback for searching, close all popups
 * @param {String} props.navVersion, Nav version, used for GA4 event nav_version reporting
 * @returns {JSX.Element}
 */
const MobileNav = ({
  history,
  navItems = List(),
  handleItemClick,
  handleSearch,
  navVersion,
  hideNavBar,
}) => {
  const header = useSelector(selectHeader)
  const location = useSelector(selectLocation)
  const testarossa = useSelector(selectTestarossa)
  const alertBar = useSelector(selectAlertBar)
  const isLoggedIn = useSelector(selectAuthorized)
  const userReferrals = useSelector(selectUserReferrals)
  const staticTextHeader = useSelector(selectStaticTextByKey('header'))

  const dispatch = useDispatch()

  useEffect(() => {
    const { document = {} } = global

    if (header.get('navigationOverlayVisible') && document?.body && document?.body.classList) {
      document.body.classList.add('navigation-primary-v2__mobile-open')
    }

    return () => {
      if (document?.body && document?.body.classList) {
        document.body.classList.remove('navigation-primary-v2__mobile-open')
      }
    }
  }, [header])

  const applyAlertBarClass = () => {
    const className = ['navigation-primary-v2__mobile']
    const isMh = location === '/'
    const alertBarVisible = alertBar.get('visible')
    const testarossaCampaigns = testarossa.getIn(['records'], Map())
    const ri97 = testarossaCampaigns.find((campaign) => campaign.getIn(['campaign', 'friendlyId']) === 'RI-97')

    if (alertBarVisible && isLoggedIn && isMh && ri97) {
      className.push('navigation-primary-v2__mobile--alertBarRi97')

      return className.join(' ')
    } if (location && headerAlertBarShouldRender(location) && alertBarVisible) {
      className.push('navigation-primary-v2__mobile--alertBar')

      return className.join(' ')
    }

    return className
  }

  const handleInviteFriendPopup = () => {
    const showingInviteFriendPopup = userReferrals.getIn(['showInviteFriendPopup'])
    dispatch(showInviteFriendPopup(!showingInviteFriendPopup))
    dispatch(setNavigationOverlayVisible(false))
    // GA4 event
    dispatch(setDefaultGa4Event(getNavClickedGa4Event(navVersion).mergeDeep({
      nav_element: 'nav item',
      nav_text: NAVIGATION_EVENT_LABELS.IAF,
    })))
  }

  if (hideNavBar) {
    return null
  }

  if (!header.get('navigationOverlayVisible')) {
    return (
      <nav
        id="navigation-primary-v2__mobile"
        className={applyAlertBarClass()}
        aria-expanded="false"
      />
    )
  }

  return (
    <div
      id="navigation-primary-v2__mobile"
      className={applyAlertBarClass()}
      aria-expanded="true"
    >
      <SearchForm
        onSubmit={handleSearch}
        history={history}
        resetOnSubmit
        hasButton
        isNav
        onFocus={() => {
          // GA4 event
          dispatch(setDefaultGa4Event(getNavClickedGa4Event(navVersion).mergeDeep({
            nav_element: 'search',
          })))
        }}
      />
      <ul className="navigation-primary-v2__primary">
        {navItems.map((item) => {
          const trackingId = item.get('trackingId', -1)

          return (
            <MobileNavItem
              key={`mobile-${trackingId}`}
              item={item}
              handleItemClick={handleItemClick}
            />
          )
        })}
        <li className="navigation-primary-v2__mobile-item">
          <TextLink
            to={'/careers'}
            text={staticTextHeader.get('careers')}
            data-href="/careers"
            target={TARGET_BLANK}
            directLink
            onClick={() => {
              // GA4 event
              dispatch(setDefaultGa4Event(getNavClickedGa4Event(navVersion).mergeDeep({
                nav_element: 'nav item',
                nav_text: NAVIGATION_EVENT_LABELS.CAREER,
              })))
            }}
            className="navigation-primary-v2__mobile-link"
          />
        </li>
        {isLoggedIn && (
          <li className="navigation-primary-v2__mobile-item">
            <Button
              type={TYPES.LINK}
              onClick={handleInviteFriendPopup}
              className="navigation-primary-v2__mobile-link"
            >
              {staticTextHeader.get('invite')}
            </Button>
          </li>
        )}
      </ul>
    </div>
  )
}

/**
 * Top line menu component
 * @param {Object} props The props
 * @param {Object} props.history The history of app
 * @param {List} props.navItems The navItems from BE
 * @param {String} props.navVersion The nav version for GA4 report
 * @returns {JSX.Element}
 */
const NavigationPrimaryV2 = ({
  history,
  navItems,
  navVersion,
  darkMode,
  hideNavBar,
}) => {
  const language = useSelector(selectUserLanguages) || List()
  const isMobile = useSelector(selectIsBreakpoint(getMobileBreakpoint(language.first())))
  const auth = useSelector(selectAuth)
  const isLoggedIn = useSelector(selectAuthorized)
  const resolver = useSelector(selectResolver)
  const location = useSelector(selectPath)
  const dispatch = useDispatch()
  const menuRef = useRef(null)

  const hideAnotherPopup = () => {
    dispatch(setUserMenuVisible(false))
    dispatch(setNotificationsMenuVisible(false))
    dispatch(setNavigationOverlayVisible(false))
  }

  const handleItemClick = (eventLabel) => {
    // GA4 event
    dispatch(setDefaultGa4Event(getNavClickedGa4Event(navVersion).mergeDeep({
      nav_element: 'nav item',
      nav_text: eventLabel,
    })))

    const gaiaScreen = createGaiaScreenModel({ resolver })
    const upstreamContext = createUpstreamContext({
      contextType: CONTEXT_TYPE_NAV_PRIMARY,
      screenParam: gaiaScreen.get('screenParam'),
      screenType: gaiaScreen.get('screenType', SCREEN_TYPE_HEADER),
    })

    if (upstreamContext && auth && location && language) {
      dispatch(clearUpstreamContext())
    }

    dispatch(setNavigationOverlayVisible(false))
    hideAnotherPopup()
  }

  const navigationClassName = [
    'navigation-primary-v2',
    'default',
    darkMode ? 'navigation-primary-v2--dark-mode' : null,
  ].filter((cls) => !!cls).join(' ')

  return (
    <nav className={navigationClassName} ref={menuRef}>
      {isMobile ? (
        <MobileNav
          navItems={navItems}
          history={history}
          handleItemClick={handleItemClick}
          handleSearch={hideAnotherPopup}
          navVersion={navVersion}
          hideNavBar={hideNavBar}
        />
      ) : (
        <DesktopNav
          navItems={navItems}
          history={history}
          handleItemClick={handleItemClick}
          handleSearch={hideAnotherPopup}
          navVersion={navVersion}
          darkMode={darkMode}
          hideNavBar={hideNavBar}
        />
      )}
      { /* isLoggedIn && <Tour isMobile={isMobile} elementRef={menuRef}/> */}
      {!!getConfig().features.announcementModal.enabled && isLoggedIn && <Announcement />}
    </nav>
  )
}

NavigationPrimaryV2.propTypes = {
  history: PropTypes.object.isRequired,
  navItems: ImmutablePropTypes.list.isRequired,
  navVersion: PropTypes.string,
  darkMode: PropTypes.bool,
  hideNavBar: PropTypes.bool,
}

export default NavigationPrimaryV2
